import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PlusIcon, TrashIcon, CheckIcon, ArchiveBoxIcon, FolderOpenIcon } from '@heroicons/react/24/outline';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '@/components/ui/tip';

type Note = {
  id: string;
  entityType: string;
  entityId: string;
  title?: string | null;
  content: string;
  noteType: string;
  status: string;
  createdAt: string;
  createdByName?: string;
};
type ClientLite = {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
};

const ENTITY_TYPES = [
  { value: 'client', label: 'Cliente' },
  { value: 'provider', label: 'Proveedor' },
  { value: 'task', label: 'Tarea' },
  { value: 'appointment', label: 'Turno' },
  { value: 'ot', label: 'OT' },
  { value: 'budget', label: 'Presupuesto' },
  { value: 'invoice', label: 'Factura' },
  { value: 'remito', label: 'Remito' },
  { value: 'diagnostic', label: 'Diagnostico' },
  { value: 'service_report', label: 'Reporte servicio' },
  { value: 'other', label: 'Otro' },
];

const NOTE_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'important', label: 'Importante' },
  { value: 'service', label: 'Servicio' },
  { value: 'payment', label: 'Pago' },
  { value: 'internal', label: 'Interna' },
  { value: 'followup', label: 'Seguimiento' },
  { value: 'technical', label: 'Tecnica' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'open', label: 'Abierta' },
  { value: 'resolved', label: 'Resuelta' },
  { value: 'archived', label: 'Archivada' },
];

const statusStyle: Record<string, string> = {
  open: 'bg-amber-100 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
};

const statusLabel: Record<string, string> = {
  open: 'Abierta',
  resolved: 'Resuelta',
  archived: 'Archivada',
};

const NotesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialEntityType = searchParams.get('entityType') || 'client';
  const initialEntityId = searchParams.get('entityId') || '';

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [entityType, setEntityType] = useState(initialEntityType);
  const [entityId, setEntityId] = useState(initialEntityId);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');

  // Form crear
  const [showForm, setShowForm] = useState(false);
  const [createEntityType, setCreateEntityType] = useState(initialEntityType);
  const [createEntityId, setCreateEntityId] = useState(initialEntityId);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [creating, setCreating] = useState(false);

  // Clientes
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [filterClientSearch, setFilterClientSearch] = useState('');

  // Detalle
  const [viewNote, setViewNote] = useState<Note | null>(null);

  const loadClients = async (search: string) => {
    try {
      const res = await axios.get('/api/clients', { params: { search: search || undefined, page: 1, limit: 50 } });
      setClients(Array.isArray(res.data?.clients) ? res.data.clients : []);
    } catch { setClients([]); }
  };

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/notes', {
        params: {
          entityType: entityType || undefined,
          entityId: entityId || undefined,
          status: status !== 'all' ? status : undefined,
          q: q || undefined,
          limit: 200,
        },
      });
      setNotes(res.data?.notes || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudieron cargar las notas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); }, []); // eslint-disable-line

  useEffect(() => {
    if (createEntityType !== 'client' && entityType !== 'client') return;
    const timer = setTimeout(() => {
      loadClients(createEntityType === 'client' ? clientSearch : filterClientSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [createEntityType, entityType, clientSearch, filterClientSearch]); // eslint-disable-line

  const canCreate = useMemo(
    () => Boolean(createEntityType && createEntityId.trim() && content.trim()),
    [createEntityType, createEntityId, content]
  );

  const createNote = async () => {
    if (!canCreate) return;
    try {
      setCreating(true);
      await axios.post('/api/notes', {
        entityType: createEntityType,
        entityId: createEntityId.trim(),
        title: title.trim() || null,
        content: content.trim(),
        noteType,
      });
      toast.success('Nota creada');
      setTitle('');
      setContent('');
      setShowForm(false);
      if (!entityId) {
        setEntityType(createEntityType);
        setEntityId(createEntityId.trim());
      }
      await fetchNotes();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo crear la nota');
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (note: Note, nextStatus: 'open' | 'resolved' | 'archived') => {
    try {
      await axios.patch(`/api/notes/${note.id}/status`, { status: nextStatus });
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, status: nextStatus } : n)));
      if (viewNote?.id === note.id) setViewNote({ ...viewNote, status: nextStatus });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo actualizar estado');
    }
  };

  const removeNote = async (note: Note) => {
    if (!await confirmDialog('¿Eliminar esta nota?')) return;
    try {
      await axios.delete(`/api/notes/${note.id}`);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      if (viewNote?.id === note.id) setViewNote(null);
      toast.success('Nota eliminada');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const clientLabel = (id: string) => {
    const c = clients.find((cl) => cl.id === id);
    if (!c) return id.slice(0, 8) + '...';
    return c.companyName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || id;
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Notas</h1>
          <p className="text-sm text-muted-foreground">Notas transversales para clientes, proveedores, tareas, OT y más.</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <PlusIcon className="h-4 w-4" />
          Nueva nota
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-40">
          <Label className="text-xs text-muted-foreground mb-1 block">Entidad</Label>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Entidad" />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_TYPES.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {entityType === 'client' ? (
          <div className="w-56">
            <Label className="text-xs text-muted-foreground mb-1 block">Cliente</Label>
            <Select value={entityId || '__all__'} onValueChange={(v) => setEntityId(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos los clientes" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input value={filterClientSearch} onChange={(e) => setFilterClientSearch(e.target.value)} placeholder="Buscar..." className="h-8" />
                </div>
                <SelectItem value="__all__">Todos los clientes</SelectItem>
                {clients.map((client) => {
                  const label = client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.id;
                  return <SelectItem key={client.id} value={client.id}>{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="w-56">
            <Label className="text-xs text-muted-foreground mb-1 block">ID entidad</Label>
            <Input value={entityId} onChange={(e) => setEntityId(e.target.value)} placeholder="Filtrar por id" className="h-9" />
          </div>
        )}

        <div className="w-36">
          <Label className="text-xs text-muted-foreground mb-1 block">Estado</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-48">
          <Label className="text-xs text-muted-foreground mb-1 block">Buscar</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Título o contenido..."
            className="h-9"
            onKeyDown={(e) => e.key === 'Enter' && fetchNotes()}
          />
        </div>

        <Button onClick={fetchNotes} variant="outline" className="h-9">Filtrar</Button>
      </div>

      {/* Tabla */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="w-28">Entidad</TableHead>
              <TableHead className="w-28">Tipo</TableHead>
              <TableHead>Título / Contenido</TableHead>
              <TableHead className="w-28">Estado</TableHead>
              <TableHead className="w-40">Fecha</TableHead>
              <TableHead className="w-32">Creado por</TableHead>
              <TableHead className="w-36 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : notes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                  No hay notas para los filtros seleccionados.
                </TableCell>
              </TableRow>
            ) : (
              notes.map((note) => (
                <TableRow
                  key={note.id}
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setViewNote(note)}
                >
                  <TableCell>
                    <Badge variant="secondary" className="text-xs capitalize">
                      {ENTITY_TYPES.find(e => e.value === note.entityType)?.label || note.entityType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground capitalize">
                      {NOTE_TYPES.find(t => t.value === note.noteType)?.label || note.noteType}
                    </span>
                  </TableCell>
                  <TableCell>
                    {note.title && <div className="font-medium text-sm">{note.title}</div>}
                    <div className="text-xs text-muted-foreground truncate max-w-xs">{note.content}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs border ${statusStyle[note.status] || ''}`}>
                      {statusLabel[note.status] || note.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(note.createdAt).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {note.createdByName || '-'}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {note.status !== 'resolved' && (
                        <Tip label="Resolver">
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" onClick={() => updateStatus(note, 'resolved')}>
                          <CheckIcon className="h-4 w-4" />
                        </Button>
                        </Tip>
                      )}
                      {note.status !== 'archived' && (
                        <Tip label="Archivar">
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-slate-700" onClick={() => updateStatus(note, 'archived')}>
                          <ArchiveBoxIcon className="h-4 w-4" />
                        </Button>
                        </Tip>
                      )}
                      {note.status !== 'open' && (
                        <Tip label="Reabrir">
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600 hover:text-amber-700" onClick={() => updateStatus(note, 'open')}>
                          <FolderOpenIcon className="h-4 w-4" />
                        </Button>
                        </Tip>
                      )}
                      <Tip label="Eliminar">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => removeNote(note)}>
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                      </Tip>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {notes.length > 0 && (
          <div className="px-4 py-2 border-t text-xs text-muted-foreground">
            Mostrando {notes.length} nota{notes.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Modal nueva nota */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nueva nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Entidad *</Label>
                <Select value={createEntityType} onValueChange={setCreateEntityType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Entidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {createEntityType === 'client' ? (
                <div className="space-y-1">
                  <Label>Cliente *</Label>
                  <Select value={createEntityId} onValueChange={setCreateEntityId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="p-2">
                        <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Buscar cliente..." className="h-8" />
                      </div>
                      {clients.map((client) => {
                        const label = client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.id;
                        return <SelectItem key={client.id} value={client.id}>{label}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1">
                  <Label>ID entidad *</Label>
                  <Input value={createEntityId} onChange={(e) => setCreateEntityId(e.target.value)} placeholder="UUID o código" />
                </div>
              )}

              <div className="space-y-1">
                <Label>Tipo</Label>
                <Select value={noteType} onValueChange={setNoteType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTE_TYPES.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label>Título (opcional)</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Resumen corto" />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Contenido *</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Escribe la nota..."
                className="min-h-[120px] resize-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={createNote} disabled={!canCreate || creating}>
                {creating ? 'Guardando...' : 'Guardar nota'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal detalle nota */}
      <Dialog open={!!viewNote} onOpenChange={(open) => !open && setViewNote(null)}>
        {viewNote && (
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{viewNote.title || 'Detalle de nota'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{ENTITY_TYPES.find(e => e.value === viewNote.entityType)?.label || viewNote.entityType}</Badge>
                <Badge variant="outline">{NOTE_TYPES.find(t => t.value === viewNote.noteType)?.label || viewNote.noteType}</Badge>
                <Badge className={`border ${statusStyle[viewNote.status] || ''}`}>{statusLabel[viewNote.status] || viewNote.status}</Badge>
              </div>
              <p className="text-sm whitespace-pre-wrap">{viewNote.content}</p>
              <p className="text-xs text-muted-foreground">
                {viewNote.createdByName || 'Usuario'} · {new Date(viewNote.createdAt).toLocaleString('es-AR')}
              </p>
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {viewNote.status !== 'open' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(viewNote, 'open')}>
                    <FolderOpenIcon className="h-4 w-4 mr-1" /> Reabrir
                  </Button>
                )}
                {viewNote.status !== 'resolved' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(viewNote, 'resolved')}>
                    <CheckIcon className="h-4 w-4 mr-1" /> Resolver
                  </Button>
                )}
                {viewNote.status !== 'archived' && (
                  <Button size="sm" variant="outline" onClick={() => updateStatus(viewNote, 'archived')}>
                    <ArchiveBoxIcon className="h-4 w-4 mr-1" /> Archivar
                  </Button>
                )}
                <Button size="sm" variant="destructive" className="ml-auto" onClick={() => removeNote(viewNote)}>
                  <TrashIcon className="h-4 w-4 mr-1" /> Eliminar
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default NotesPage;
